<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Sparkles,
  ArrowUp,
  ArrowDown,
  Wand2,
  Check,
  X,
  Stars,
  ArrowRight,
  Sun,
  Moon,
  Monitor,
  Code2,
  Power,
  Pause,
  CircleDot,
  ListPlus,
  Layers,
  Package,
  History,
  Plus,
  Trash2,
  Mic,
  Keyboard,
} from 'lucide-vue-next';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/auth';
import { useCurrentProjectId } from '@/composables/useCurrentProjectId';
import { useFrontendProposalApplier } from '@/composables/useFrontendProposalApplier';
import { useUiState } from '@/composables/useUiState';
import AssistantMarkdown from '@/components/copilot/AssistantMarkdown.vue';
import SimulatedCallBubbles from '@/components/copilot/SimulatedCallBubbles.vue';
import SimulatedCallPairCard from '@/components/copilot/SimulatedCallPairCard.vue';
import { normalizeClarificationSuggestions } from '@/lib/copilot-proposal-rendering';
import {
  useCopilotConversation,
  type ProposalState,
  type UICopilotMessage,
} from '@/composables/useCopilotConversation';
import {
  applyBackendProposal,
  getCopilotUsage,
  setAssistantChannel,
  streamCopilotMessages,
  type Proposal,
  type FrontendProposal,
  type BackendProposal,
  type SimulatedCallTranscriptProposal,
  type UsageInfo,
} from '@/api/copilot';
import { cn } from '@/lib/cn';

const { t, te, locale } = useI18n();
const dateLocale = computed(() =>
  locale.value === 'ru' ? 'ru-RU' : locale.value === 'es' ? 'es-ES' : 'en-US',
);

const ui = useUiStore();
const auth = useAuthStore();
const currentProjectId = useCurrentProjectId();
const frontendApplier = useFrontendProposalApplier();
const uiStateRef = useUiState();
const conv = useCopilotConversation();

/**
 * Per ADR-0041 / philosophy §1.1. The user's chosen channel preference,
 * mirrored from `auth.user.assistantChannel`. `null` means "not yet
 * asked" — the Sheet renders a channel-pick screen instead of the
 * conversation list. v1 voice is a stub: picking voice writes the
 * preference but the conversation pane shows a "голос скоро" panel with
 * a toggle back to text. Real LiveKit-room voice for the assistant is a
 * separate epic listed under §9 Open.
 */
const assistantChannel = computed<'text' | 'voice' | null>(
  () => auth.user?.assistantChannel ?? null,
);
const channelSaving = ref(false);

async function pickChannel(channel: 'text' | 'voice'): Promise<void> {
  if (channelSaving.value) return;
  channelSaving.value = true;
  try {
    const updated = await setAssistantChannel(channel);
    if (auth.user) {
      auth.setUser({ ...auth.user, assistantChannel: updated.assistantChannel });
    }
    if (channel === 'text' && open.value) {
      // Mirror the open-the-sheet eager-load path: when the user lands
      // on text (first pick or back-to-text from the voice stub), pull
      // history and resolve the canonical general thread.
      await nextTick();
      inputEl.value?.focus();
      void conv.refreshList();
      void refreshUsage();
      if (conv.currentId.value && conv.messages.value.length === 0) {
        try {
          await conv.loadConversation(conv.currentId.value);
        } catch {
          try {
            await conv.resolveGeneralFromServer();
          } catch {
            conv.startNew();
          }
        }
      } else if (!conv.currentId.value && conv.messages.value.length === 0) {
        try {
          await conv.resolveGeneralFromServer();
        } catch {
          // best-effort
        }
      }
    }
  } catch {
    // Best-effort: leave the pick screen open so the user can retry.
  } finally {
    channelSaving.value = false;
  }
}

const input = ref('');
const inputEl = ref<HTMLTextAreaElement | null>(null);
const listEl = ref<HTMLDivElement | null>(null);
const streaming = ref(false);
let cancelStream: (() => void) | null = null;

// Token-quota state surfaced as a small badge.
const usage = ref<UsageInfo | null>(null);
const quotaBlocked = ref<{ message: string; resetAt?: string } | null>(null);

const usagePercent = computed(() => {
  if (!usage.value || usage.value.limit <= 0) return 0;
  return Math.min(100, Math.round((usage.value.used / usage.value.limit) * 100));
});
const usageVariant = computed(() => {
  const p = usagePercent.value;
  if (p >= 95) return 'danger' as const;
  if (p >= 75) return 'warning' as const;
  return 'neutral' as const;
});

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return String(n);
}

async function refreshUsage(): Promise<void> {
  try {
    usage.value = await getCopilotUsage();
  } catch {
    usage.value = null;
  }
}

// Local alias so the rest of the template/script reads the same as before.
const messages = conv.messages;

const open = computed({
  get: () => ui.copilotOpen,
  set: (value: boolean) => {
    if (value) ui.openCopilot();
    else ui.closeCopilot();
  },
});

const suggestions = computed<string[]>(() =>
  currentProjectId.value
    ? [
        t('components.copilot.suggestions.todayCalls'),
        t('components.copilot.suggestions.darkTheme'),
        t('components.copilot.suggestions.renameBusiness'),
        t('components.copilot.suggestions.pauseAgent'),
      ]
    : [
        t('components.copilot.suggestions.whatCanYouDo'),
        t('components.copilot.suggestions.openHome'),
        t('components.copilot.suggestions.enableDarkTheme'),
        t('components.copilot.suggestions.createAgent'),
      ],
);

watch(open, async (value) => {
  if (value) {
    await nextTick();
    // Per ADR-0041. Until the user picks a channel (assistantChannel ==
    // null) the sheet renders the pick screen — no composer to focus, no
    // history to refresh. The voice-stub surface is similar: no
    // conversation pane, no composer focus. Only the text path needs the
    // eager load-on-open dance.
    if (assistantChannel.value !== 'text') {
      return;
    }
    inputEl.value?.focus();
    // Refresh history list every time the sheet opens.
    void conv.refreshList();
    void refreshUsage();
    // Eagerly load the persisted conversation if we have an id but no
    // messages in memory (e.g. fresh page load). Per ADR-0044 / philosophy
    // §1.5, if the cached id fails to load (different device, deleted
    // elsewhere, etc.) we ask the server for the user's canonical
    // general-conversation thread instead of starting a fresh local one
    // — that way "one assistant identity" holds across devices.
    if (conv.currentId.value && conv.messages.value.length === 0) {
      try {
        await conv.loadConversation(conv.currentId.value);
      } catch {
        try {
          await conv.resolveGeneralFromServer();
        } catch {
          // Server resolve also failed (offline, auth glitch) — fall back
          // to a fresh chat without surfacing the error.
          conv.startNew();
        }
      }
    } else if (!conv.currentId.value && conv.messages.value.length === 0) {
      // No cached id at all (fresh sign-in on this device). Resolve from
      // server so this device joins the user's existing thread instead of
      // implicitly forking a new one on first turn.
      try {
        await conv.resolveGeneralFromServer();
      } catch {
        // Best-effort — the existing fresh-chat path still works as a
        // fallback when the user sends a turn.
      }
    }

    // Editor-side hand-off ("Спросить помощника" buttons in editors set a
    // one-shot seed). We pre-fill the composer; the user still presses
    // Enter to send. Optionally starts a fresh chat first so the seed
    // doesn't get appended to an unrelated conversation.
    const seed = conv.consumeSeed();
    if (seed) {
      if (seed.startNew) conv.startNew();
      input.value = seed.message;
      await nextTick();
      autoSize();
    }
  }
});

onBeforeUnmount(() => {
  cancelStream?.();
});

function autoSize(): void {
  const el = inputEl.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
}

function scrollToBottom(): void {
  const el = listEl.value;
  if (!el) return;
  requestAnimationFrame(() => {
    el.scrollTop = el.scrollHeight;
  });
}

// Sum of pending/applying proposals across the whole conversation. Drives
// the header indicator that keeps the "N actions waiting" affordance
// visible even when the matching cards scrolled off — the most common
// reason users miss them after a long assistant reply.
const pendingProposalCount = computed(() => {
  let n = 0;
  for (const m of messages.value) {
    if (!m.proposals) continue;
    for (const p of m.proposals) {
      if (p.status === 'pending' || p.status === 'applying') n += 1;
    }
  }
  return n;
});

/**
 * Per ADR-0047 §"Before/after pair". Folds the message's proposals into a
 * render plan where matched `before`/`after` simulated-call halves collapse
 * into one comparison entry. Non-paired proposals pass through unchanged,
 * preserving the existing per-state card layout and apply/reject flow.
 */
type SimulatedCall = SimulatedCallTranscriptProposal;

type RenderEntry =
  | { kind: 'state'; state: ProposalState; uid: string }
  | { kind: 'pair'; before: SimulatedCall; after: SimulatedCall; uid: string };

function renderPlan(m: UICopilotMessage): RenderEntry[] {
  const states = m.proposals ?? [];
  const plan: RenderEntry[] = [];
  const pending = new Map<string, { proposal: SimulatedCall; index: number }>();
  for (const state of states) {
    const p = state.proposal;
    if (
      p.kind === 'simulated_call_transcript' &&
      p.pairId &&
      (p.variant === 'before' || p.variant === 'after')
    ) {
      const existing = pending.get(p.pairId);
      if (existing && existing.proposal.variant !== p.variant) {
        const before = p.variant === 'before' ? p : existing.proposal;
        const after = p.variant === 'after' ? p : existing.proposal;
        plan[existing.index] = {
          kind: 'pair',
          before,
          after,
          uid: `pair-${p.pairId}`,
        };
        pending.delete(p.pairId);
        continue;
      }
      pending.set(p.pairId, { proposal: p, index: plan.length });
      plan.push({
        kind: 'state',
        state,
        uid: state.proposal.proposalId ?? `s-${plan.length}`,
      });
      continue;
    }
    plan.push({
      kind: 'state',
      state,
      uid: state.proposal.proposalId ?? `s-${plan.length}`,
    });
  }
  return plan;
}

function isLonePairHalf(p: SimulatedCallTranscriptProposal): boolean {
  return Boolean(p.pairId) && (p.variant === 'before' || p.variant === 'after');
}

// Scrolls the first pending proposal card into view. Used by the header
// indicator. `block: 'center'` so the user sees the whole card (header +
// preview + Apply footer) rather than just its top edge.
function scrollToFirstPending(): void {
  const root = listEl.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>('[data-proposal-status="pending"]');
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

async function send(): Promise<void> {
  const text = input.value.trim();
  if (!text || streaming.value) return;

  const userMsg: UICopilotMessage = {
    id: crypto.randomUUID(),
    role: 'user',
    content: text,
  };
  const assistantMsg: UICopilotMessage = {
    id: crypto.randomUUID(),
    role: 'assistant',
    content: '',
    pending: true,
    proposals: [],
  };
  messages.value.push(userMsg, assistantMsg);
  input.value = '';
  await nextTick();
  autoSize();
  scrollToBottom();

  streaming.value = true;
  const snapshot = uiStateRef.value;
  // Per ADR-0041. We stamp the current toggle value on the turn so the
  // server persists `CopilotMessage.channel` per-turn. v1 voice never
  // streams a turn through here (the pane shows the voice-stub instead),
  // so the value is effectively always 'text' today — but the plumbing
  // is end-to-end ready for the LiveKit-room voice epic.
  const channelForTurn: 'text' | 'voice' = assistantChannel.value === 'voice' ? 'voice' : 'text';
  const handle = streamCopilotMessages({
    ...(conv.currentId.value ? { conversationId: conv.currentId.value } : {}),
    ...(currentProjectId.value ? { projectId: currentProjectId.value } : {}),
    ...(Object.keys(snapshot).length > 0 ? { uiState: snapshot } : {}),
    channel: channelForTurn,
    newUserMessage: text,
  });
  cancelStream = handle.cancel;

  try {
    for await (const ev of handle.events) {
      if (ev.kind === 'conversation_created') {
        conv.currentId.value = ev.conversationId;
      } else if (ev.kind === 'text_delta') {
        assistantMsg.toolRunning = undefined;
        assistantMsg.content += ev.delta;
        scrollToBottom();
      } else if (ev.kind === 'tool_running') {
        assistantMsg.toolRunning = ev.tool;
      } else if (ev.kind === 'proposal') {
        // Per ADR-0043 / philosophy §2.3. Server-tagged stakesTier === 'low'
        // is the silent-apply path: we apply the frontend-scope proposal
        // immediately and render a slim "Готово / Отменить" card driven by
        // the undo callback returned by the applier. Everything else lands
        // in the regular `pending` state and waits for the user to apply.
        const proposal = ev.proposal;
        const state: ProposalState = { proposal, status: 'pending' };
        assistantMsg.proposals?.push(state);
        scrollToBottom();
        if (proposal.stakesTier === 'low' && proposal.scope === 'frontend') {
          void (async () => {
            try {
              const undo = await frontendApplier.apply(proposal as FrontendProposal);
              state.status = 'silent_applied';
              state.undo = undo;
            } catch (err: unknown) {
              state.status = 'failed';
              state.error = err instanceof Error ? err.message : String(err);
            }
          })();
        }
      } else if (ev.kind === 'error') {
        if (ev.code === 'RATE_LIMIT') {
          const resetAt = typeof ev.details?.resetAt === 'string' ? ev.details.resetAt : undefined;
          quotaBlocked.value = { message: ev.message, ...(resetAt ? { resetAt } : {}) };
          assistantMsg.content =
            (assistantMsg.content || '') +
            (assistantMsg.content ? '\n\n' : '') +
            `⚠️ ${t('components.copilot.quotaExhaustedInline', { when: formatResetTime(resetAt) })}`;
        } else {
          assistantMsg.content =
            (assistantMsg.content || '') +
            (assistantMsg.content ? '\n\n' : '') +
            `⚠️ ${ev.message}`;
        }
      } else if (ev.kind === 'done') {
        break;
      }
    }
  } finally {
    assistantMsg.pending = false;
    assistantMsg.toolRunning = undefined;
    streaming.value = false;
    cancelStream = null;
    scrollToBottom();
    // Title or updatedAt changed on the server — refresh the dropdown.
    void conv.refreshList();
    void refreshUsage();
  }
}

function formatResetTime(iso?: string): string {
  if (!iso) return t('components.copilot.tomorrow');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return t('components.copilot.tomorrow');
  const now = new Date();
  const diffH = Math.max(0, Math.round((d.getTime() - now.getTime()) / 3_600_000));
  if (diffH < 1) return t('components.copilot.inAnHour');
  if (diffH < 24) return t('components.copilot.inHours', { count: diffH });
  return d.toLocaleString(dateLocale.value, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

async function selectConversation(id: string): Promise<void> {
  if (streaming.value) return;
  if (id === conv.currentId.value) return;
  try {
    await conv.loadConversation(id);
    await nextTick();
    scrollToBottom();
  } catch {
    // ignore — list will refresh on next sheet open
  }
}

function newChat(): void {
  if (streaming.value) return;
  conv.startNew();
  void nextTick(() => inputEl.value?.focus());
}

async function deleteCurrent(): Promise<void> {
  if (!conv.currentId.value || streaming.value) return;
  if (!confirm(t('components.copilot.deleteConversationConfirm'))) return;
  try {
    await conv.remove(conv.currentId.value);
  } catch {
    // ignore
  }
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay)
    return d.toLocaleTimeString(dateLocale.value, { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString(dateLocale.value, { day: '2-digit', month: '2-digit' });
}

function applySuggestion(s: string): void {
  input.value = s;
  void nextTick(() => inputEl.value?.focus());
}

async function onApply(state: ProposalState): Promise<void> {
  if (state.status !== 'pending') return;
  state.status = 'applying';
  try {
    if (state.proposal.scope === 'backend') {
      await applyBackendProposal(state.proposal as BackendProposal);
    } else {
      // Per ADR-0043, the applier returns an undo callback (or null).
      // Manual-apply paths discard it — silent-apply is what surfaces undo.
      await frontendApplier.apply(state.proposal as FrontendProposal);
    }
    state.status = 'applied';
  } catch (err: unknown) {
    state.status = 'failed';
    state.error = err instanceof Error ? err.message : String(err);
  }
}

/**
 * Per ADR-0045 v1 + ADR-0063 v2. Forecast-strip helpers — numeric cents
 * instead of the qualitative direction. Near-zero (|delta| < 0.05¢) is
 * framed as "no change" without surfacing the number; absolute deltas
 * are rendered to one decimal with the sign. Emojis act as colorless
 * icons (no semantic-color dependency).
 */
const COST_NOOP_THRESHOLD_CENTS = 0.05;

function isCostNoop(cents: number): boolean {
  return Math.abs(cents) < COST_NOOP_THRESHOLD_CENTS;
}

function costEmoji(cents: number): string {
  if (isCostNoop(cents)) return '≈';
  if (cents < 0) return '💰';
  if (cents >= 1) return '💸';
  return '💲';
}

function costLabel(cents: number): string {
  if (isCostNoop(cents)) {
    return t('components.copilot.forecast.costNoChange');
  }
  const formatted = formatCentsDelta(cents);
  return t('components.copilot.forecast.costPerCall', { amount: formatted });
}

function formatCentsDelta(cents: number): string {
  const sign = cents > 0 ? '+' : '−';
  const abs = Math.abs(cents);
  // One decimal place when the magnitude is below 10¢, integer above.
  const digits = abs < 10 ? 1 : 0;
  return `${sign}${abs.toFixed(digits)}¢`;
}

function confidenceQualifier(c: 'low' | 'medium' | 'high'): string {
  return c === 'low' ? t('components.copilot.forecast.confidenceLow') : '';
}

function riskEmoji(d: 'no_change' | 'increased_refusal' | 'reduced_refusal'): string {
  switch (d) {
    case 'increased_refusal':
      return '⚠️';
    case 'reduced_refusal':
      return '✓';
    case 'no_change':
    default:
      return '≈';
  }
}

function riskLabel(d: 'no_change' | 'increased_refusal' | 'reduced_refusal'): string {
  switch (d) {
    case 'increased_refusal':
      return t('components.copilot.forecast.riskUp');
    case 'reduced_refusal':
      return t('components.copilot.forecast.riskDown');
    case 'no_change':
    default:
      return t('components.copilot.forecast.riskNoChange');
  }
}

async function onUndo(state: ProposalState): Promise<void> {
  if (state.status !== 'silent_applied' || !state.undo) return;
  try {
    await state.undo();
    state.status = 'reverted';
  } catch (err: unknown) {
    state.status = 'failed';
    state.error = err instanceof Error ? err.message : String(err);
  }
}

function onReject(state: ProposalState): void {
  if (state.status === 'pending') state.status = 'rejected';
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    void send();
  }
}

function proposalKindLabel(p: Proposal): string {
  switch (p.kind) {
    case 'business_data_update':
      return t('components.copilot.proposalKind.businessData');
    case 'scenario_prompt_update':
      return t('components.copilot.proposalKind.scenarioPrompt');
    case 'project_status_change':
      return t('components.copilot.proposalKind.projectStatus');
    case 'scenario_pricing_tier_change':
      return t('components.copilot.proposalKind.pricingTier');
    case 'add_slot':
      return t('components.copilot.proposalKind.addSlot');
    case 'create_scenario':
      return t('components.copilot.proposalKind.createScenario');
    case 'apply_industry_pack':
      return t('components.copilot.proposalKind.industryPack');
    case 'create_script':
      return t('components.copilot.proposalKind.createScript');
    case 'update_script':
      return t('components.copilot.proposalKind.updateScript');
    case 'create_step':
      return t('components.copilot.proposalKind.createStep');
    case 'update_step':
      return t('components.copilot.proposalKind.updateStep');
    case 'navigate':
      return t('components.copilot.proposalKind.navigate');
    case 'set_theme':
      return t('components.copilot.proposalKind.theme');
    case 'toggle_developer_mode':
      return t('components.copilot.proposalKind.developerMode');
    case 'simulated_call_transcript':
      // Per ADR-0047. The simulated-call transcript is now available in
      // 'general' mode (post-creation Copilot) for explain-in-reverse, not
      // only during onboarding. The template renders the bubble below.
      return t('components.copilot.proposalKind.simulatedCall');
    case 'artifact_walkthrough':
      // Per ADR-0047 §"Explain-in-reverse". Composite card: business
      // summary + nested standalone simulated call. Template renders
      // the prose above the existing simulated-call bubble strip.
      return t('components.copilot.proposalKind.artifactWalkthrough');
    case 'clarification_question':
      return t('components.copilot.proposalKind.clarification');
    case 'goals_and_pack':
    case 'test_call_summary':
    case 'goal_creation':
    case 'plugin_wiring':
      // These remain mode-specific (goals_dialog / goal_creation /
      // plugin_creation): proposal-bearing terminals of the onboarding
      // and recursive-creation flows + ADR-0049 test-call result card.
      // The general AICopilotSheet never sees them — fall through to a
      // generic label rather than adding i18n keys for surfaces this
      // sheet doesn't render.
      return p.kind;
  }
}

function clarificationSuggestions(suggestions: unknown): string[] {
  return normalizeClarificationSuggestions(suggestions);
}

function navigateLabel(target: string): string {
  switch (target) {
    case 'dashboard':
      return t('components.copilot.navigate.dashboard');
    case 'quickstart':
      return t('components.copilot.navigate.quickstart');
    case 'project_overview':
      return t('components.copilot.navigate.projectOverview');
    case 'project_agent':
      return t('components.copilot.navigate.projectAgent');
    case 'project_calls':
      return t('components.copilot.navigate.projectCalls');
    case 'project_connections':
      return t('components.copilot.navigate.projectConnections');
    case 'project_settings':
      return t('components.copilot.navigate.projectSettings');
    case 'project_goal_new':
      return t('components.copilot.navigate.projectGoalNew');
    case 'project_plugin_new':
      return t('components.copilot.navigate.projectPluginNew');
    default:
      return target;
  }
}

function statusLabel(s: 'draft' | 'active' | 'paused'): string {
  if (s === 'active') return t('common.active');
  if (s === 'paused') return t('common.paused');
  return t('common.draft');
}

function themeLabel(theme: 'light' | 'dark' | 'auto'): string {
  if (theme === 'light') return t('topbar.themeLight');
  if (theme === 'dark') return t('topbar.themeDark');
  return t('topbar.themeAuto');
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function toolLabel(name: string): string {
  const key = `components.copilot.tool.${name}`;
  return te(key) ? t(key) : name;
}

function dataTypeLabel(dt: string): string {
  switch (dt) {
    case 'string':
      return t('components.copilot.dataType.string');
    case 'integer':
      return t('components.copilot.dataType.integer');
    case 'datetime':
      return t('components.copilot.dataType.datetime');
    case 'enum_value':
      return t('components.copilot.dataType.enumValue');
    case 'phone':
      return t('components.copilot.dataType.phone');
    case 'email':
      return t('components.copilot.dataType.email');
    case 'boolean':
      return t('components.copilot.dataType.boolean');
    default:
      return dt;
  }
}
</script>

<template>
  <Sheet
    v-model:open="open"
    side="right"
    width-class="w-full max-w-md"
    :title="t('components.copilot.title')"
    :description="
      currentProjectId
        ? t('components.copilot.descriptionWithProject')
        : t('components.copilot.descriptionWithoutProject')
    "
  >
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-2 mt-2 w-full">
        <div class="flex flex-wrap items-center gap-2 min-w-0">
          <span
            class="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-accent/10 text-accent shrink-0"
          >
            <Sparkles class="h-4 w-4" />
          </span>
          <Badge v-if="currentProjectId" variant="accent">{{
            t('components.copilot.projectConnected')
          }}</Badge>
          <Badge v-else variant="warning">{{ t('components.copilot.generalMode') }}</Badge>
          <Badge
            v-if="usage && usage.enforced"
            :variant="usageVariant"
            :title="t('components.copilot.tokensTooltip', { used: usage.used, limit: usage.limit })"
          >
            {{ formatTokens(usage.used) }} / {{ formatTokens(usage.limit) }}
          </Badge>
          <!-- Pending-actions indicator. A long assistant reply followed
               by tall proposal cards used to push the Apply buttons below
               the fold — users missed them. The chip stays in the header
               and jumps to the first pending card on click. -->
          <button
            v-if="pendingProposalCount > 0"
            type="button"
            class="inline-flex h-7 items-center gap-1 px-2 rounded-md text-[12px] font-medium bg-accent/10 text-accent border border-accent/30 hover:bg-accent/15 transition-colors animate-pulse-soft"
            :aria-label="
              t('components.copilot.pendingActions.ariaLabel', { count: pendingProposalCount })
            "
            data-testid="copilot-pending-actions"
            @click="scrollToFirstPending"
          >
            <ArrowDown class="h-3.5 w-3.5" />
            {{ t('components.copilot.pendingActions.label', { count: pendingProposalCount }) }}
          </button>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <!-- Per ADR-0041 / philosophy §1.1. Persistent channel toggle.
               Hidden until the user picked once (assistantChannel != null).
               One click flips the preference; the next turn rides on it. -->
          <div
            v-if="assistantChannel !== null"
            class="copilot-channel-toggle inline-flex items-center rounded-lg border border-border bg-bg p-0.5 shrink-0"
            :aria-label="t('components.copilot.channelToggle.ariaLabel')"
            role="group"
          >
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1.5 px-2 rounded-md text-[12px] font-medium transition-colors"
              :class="
                assistantChannel === 'text'
                  ? 'bg-surface text-text shadow-soft'
                  : 'text-text-muted hover:text-text'
              "
              :disabled="channelSaving"
              :aria-pressed="assistantChannel === 'text'"
              data-channel="text"
              @click="pickChannel('text')"
            >
              <Keyboard class="h-3.5 w-3.5" />
              {{ t('components.copilot.channelToggle.text') }}
            </button>
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1.5 px-2 rounded-md text-[12px] font-medium transition-colors"
              :class="
                assistantChannel === 'voice'
                  ? 'bg-surface text-text shadow-soft'
                  : 'text-text-muted hover:text-text'
              "
              :disabled="channelSaving"
              :aria-pressed="assistantChannel === 'voice'"
              data-channel="voice"
              @click="pickChannel('voice')"
            >
              <Mic class="h-3.5 w-3.5" />
              {{ t('components.copilot.channelToggle.voice') }}
            </button>
          </div>
          <DropdownMenu align="end">
            <template #trigger>
              <button
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-bg hover:text-text transition-colors"
                :aria-label="t('components.copilot.history')"
              >
                <History class="h-4 w-4" />
              </button>
            </template>
            <DropdownMenuLabel>{{ t('components.copilot.history') }}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem @select.prevent="newChat">
              <Plus class="h-3.5 w-3.5 text-text-muted" />
              {{ t('components.copilot.newChat') }}
            </DropdownMenuItem>
            <template v-if="conv.conversations.value.length > 0">
              <DropdownMenuSeparator />
              <DropdownMenuItem
                v-for="c in conv.conversations.value"
                :key="c.id"
                @select.prevent="selectConversation(c.id)"
              >
                <span
                  class="h-1.5 w-1.5 rounded-full shrink-0"
                  :class="c.id === conv.currentId.value ? 'bg-accent' : 'bg-transparent'"
                />
                <span class="flex-1 truncate">{{ c.title }}</span>
                <span class="text-[10px] text-text-muted shrink-0">{{
                  shortDate(c.updatedAt)
                }}</span>
              </DropdownMenuItem>
            </template>
            <template v-if="conv.currentId.value">
              <DropdownMenuSeparator />
              <DropdownMenuItem @select.prevent="deleteCurrent">
                <Trash2 class="h-3.5 w-3.5 text-text-muted" />
                <span class="text-text-muted">{{ t('components.copilot.deleteCurrent') }}</span>
              </DropdownMenuItem>
            </template>
          </DropdownMenu>
          <button
            type="button"
            class="inline-flex h-8 w-8 items-center justify-center rounded-lg text-text-muted hover:bg-bg hover:text-text transition-colors"
            :aria-label="t('components.copilot.newChat')"
            :disabled="streaming"
            @click="newChat"
          >
            <Plus class="h-4 w-4" />
          </button>
        </div>
      </div>
    </template>

    <!-- Per ADR-0041 / philosophy §1.1. Channel-pick screen — shown on the
         user's very first Copilot open (assistantChannel == null) before
         any conversation surface is rendered. Once picked the preference
         is stored on the user and this screen never reappears (the header
         toggle handles future switches). -->
    <div
      v-if="assistantChannel === null"
      class="flex flex-col items-stretch gap-4 mt-6 pb-6"
      data-testid="copilot-channel-pick"
    >
      <h2 class="text-[18px] font-medium text-text text-center px-2">
        {{ t('components.copilot.channelPick.headline') }}
      </h2>
      <p class="text-caption text-text-muted text-center">
        {{ t('components.copilot.channelPick.subhead') }}
      </p>
      <div class="flex flex-col gap-3 mt-2">
        <button
          type="button"
          class="group flex items-center gap-4 text-left p-4 rounded-2xl border border-border bg-surface hover:border-border-strong hover:bg-bg transition-all"
          :disabled="channelSaving"
          data-channel-pick="voice"
          @click="pickChannel('voice')"
        >
          <span
            class="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent shrink-0"
          >
            <Mic class="h-6 w-6" />
          </span>
          <span class="flex-1">
            <span class="block text-[15px] font-medium text-text">
              {{ t('components.copilot.channelPick.voiceTitle') }}
            </span>
            <span class="block text-caption text-text-muted mt-0.5">
              {{ t('components.copilot.channelPick.voiceHint') }}
            </span>
          </span>
        </button>
        <button
          type="button"
          class="group flex items-center gap-4 text-left p-4 rounded-2xl border border-border bg-surface hover:border-border-strong hover:bg-bg transition-all"
          :disabled="channelSaving"
          data-channel-pick="text"
          @click="pickChannel('text')"
        >
          <span
            class="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent shrink-0"
          >
            <Keyboard class="h-6 w-6" />
          </span>
          <span class="flex-1">
            <span class="block text-[15px] font-medium text-text">
              {{ t('components.copilot.channelPick.textTitle') }}
            </span>
            <span class="block text-caption text-text-muted mt-0.5">
              {{ t('components.copilot.channelPick.textHint') }}
            </span>
          </span>
        </button>
      </div>
      <p v-if="channelSaving" class="text-caption text-text-muted text-center">
        {{ t('components.copilot.channelPick.saving') }}
      </p>
    </div>

    <!-- Quota banner — only when blocked, not for normal usage -->
    <div
      v-if="assistantChannel !== null && quotaBlocked"
      class="mt-3 mb-1 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 flex items-start gap-3"
    >
      <span class="text-warning">⚠️</span>
      <div class="flex-1 text-caption">
        <p class="text-text font-medium">{{ t('components.copilot.quotaExhaustedTitle') }}</p>
        <p class="text-text-muted mt-0.5">
          <i18n-t keypath="components.copilot.quotaExhaustedHint" tag="span">
            <template #when>{{ formatResetTime(quotaBlocked.resetAt) }}</template>
            <template #envvar>
              <code class="font-mono text-[11px] bg-bg px-1 py-0.5 rounded"
                >COPILOT_DAILY_TOKEN_LIMIT</code
              >
            </template>
          </i18n-t>
        </p>
      </div>
    </div>

    <!-- Per ADR-0041 v1. Voice surface is a stub: pick lands, preference
         persists, but the conversation pane shows a "голос скоро" panel
         with a one-click back-to-text. Real LiveKit-room voice for the
         assistant is a separate epic — see philosophy §9 Open. -->
    <div
      v-if="assistantChannel === 'voice'"
      class="flex flex-col items-stretch gap-3 mt-6 pb-6"
      data-testid="copilot-voice-stub"
    >
      <div
        class="flex flex-col items-center gap-3 px-4 py-8 rounded-2xl border border-border bg-surface"
      >
        <span
          class="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent"
        >
          <Mic class="h-7 w-7" />
        </span>
        <h3 class="text-[17px] font-medium text-text text-center">
          {{ t('components.copilot.voiceStub.title') }}
        </h3>
        <p class="text-caption text-text-muted text-center max-w-xs">
          {{ t('components.copilot.voiceStub.body') }}
        </p>
        <Button
          variant="ghost"
          size="sm"
          class="mt-1"
          :disabled="channelSaving"
          data-channel-pick="text"
          @click="pickChannel('text')"
        >
          <Keyboard class="h-4 w-4 mr-1.5" />
          {{ t('components.copilot.voiceStub.backToText') }}
        </Button>
      </div>
    </div>

    <div
      v-if="assistantChannel === 'text'"
      ref="listEl"
      class="flex flex-col gap-4 pb-4 min-h-[40vh]"
    >
      <div v-if="messages.length === 0" class="flex flex-col gap-3 mt-2">
        <p class="text-[14px] text-text-muted">{{ t('components.copilot.tryItPrefix') }}</p>
        <button
          v-for="s in suggestions"
          :key="s"
          class="group flex items-start gap-3 text-left p-3 rounded-xl border border-border bg-surface hover:border-border-strong hover:bg-bg transition-all text-[14px] text-text"
          @click="applySuggestion(s)"
        >
          <Wand2 class="h-4 w-4 mt-0.5 text-accent shrink-0" />
          <span>{{ s }}</span>
        </button>
      </div>

      <template v-for="m in messages" :key="m.id">
        <div
          v-if="m.content || (m.role === 'assistant' && m.pending)"
          :class="[
            'rounded-2xl px-4 py-3 text-[14px] leading-relaxed',
            m.role === 'user'
              ? 'self-end bg-accent text-accent-fg max-w-[85%] shadow-soft whitespace-pre-wrap'
              : 'self-start bg-surface border border-border text-text max-w-[90%]',
          ]"
        >
          <span v-if="m.pending && !m.content" class="inline-flex gap-1 items-center">
            <span
              v-if="m.toolRunning"
              class="text-text-muted text-caption inline-flex items-center gap-2"
            >
              <Stars class="h-3.5 w-3.5 animate-pulse-soft" />
              {{ toolLabel(m.toolRunning) }}…
            </span>
            <template v-else>
              <span class="h-1.5 w-1.5 rounded-full bg-text-muted animate-pulse-soft" />
              <span
                class="h-1.5 w-1.5 rounded-full bg-text-muted animate-pulse-soft"
                style="animation-delay: 200ms"
              />
              <span
                class="h-1.5 w-1.5 rounded-full bg-text-muted animate-pulse-soft"
                style="animation-delay: 400ms"
              />
            </template>
          </span>
          <!-- Per ADR-0052. Assistant text routes through the markdown
               renderer so `[label](#proposal-XYZ)` anchors become inline
               ActionChips bound to the matching ProposalState in this
               message's own proposals array. User text stays plain. -->
          <template v-else-if="m.role === 'assistant'">
            <AssistantMarkdown :source="m.content" :proposals="m.proposals" />
          </template>
          <template v-else>{{ m.content }}</template>
        </div>

        <template v-if="m.role === 'assistant' && m.proposals && m.proposals.length > 0">
          <template v-for="entry in renderPlan(m)" :key="entry.uid">
            <!-- Per ADR-0047 §"Before/after pair". Two simulated-call halves
                 sharing a pairId collapse into one comparison card outside
                 the standard ProposalState wrapper — there's no apply/reject
                 footer to preserve (simulated calls are pure previews). -->
            <SimulatedCallPairCard
              v-if="entry.kind === 'pair'"
              :before="entry.before"
              :after="entry.after"
              class="self-start max-w-[95%] w-full"
            />
          <template v-else>
          <div
            v-for="state in [entry.state]"
            :key="state.proposal.proposalId ?? entry.uid"
            :data-proposal-status="state.status"
            :data-proposal-card-id="state.proposal.proposalId"
            :class="
              cn(
                'self-start max-w-[95%] w-full rounded-2xl border bg-surface shadow-lift overflow-hidden animate-slide-in-up',
                state.status === 'applied' && 'border-success/40',
                state.status === 'silent_applied' && 'border-success/40',
                state.status === 'reverted' && 'border-border opacity-60',
                state.status === 'rejected' && 'border-border opacity-60',
                state.status === 'failed' && 'border-danger/40',
                (state.status === 'pending' || state.status === 'applying') && 'border-accent-line',
              )
            "
          >
            <header
              class="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border bg-bg-2/60"
            >
              <span class="inline-flex items-center gap-2">
                <span
                  class="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-accent-soft border border-accent-line"
                >
                  <component
                    :is="
                      state.proposal.kind === 'navigate'
                        ? ArrowRight
                        : state.proposal.kind === 'set_theme'
                          ? state.proposal.theme === 'dark'
                            ? Moon
                            : state.proposal.theme === 'light'
                              ? Sun
                              : Monitor
                          : state.proposal.kind === 'toggle_developer_mode'
                            ? Code2
                            : state.proposal.kind === 'project_status_change'
                              ? state.proposal.status === 'active'
                                ? Power
                                : state.proposal.status === 'paused'
                                  ? Pause
                                  : CircleDot
                              : state.proposal.kind === 'scenario_pricing_tier_change'
                                ? Sparkles
                                : state.proposal.kind === 'add_slot'
                                  ? ListPlus
                                  : state.proposal.kind === 'create_scenario'
                                    ? Layers
                                    : state.proposal.kind === 'apply_industry_pack'
                                      ? Package
                                      : Sparkles
                    "
                    class="h-4 w-4 text-accent"
                  />
                </span>
                <span class="text-caption font-medium text-text">{{
                  proposalKindLabel(state.proposal)
                }}</span>
                <Badge v-if="state.proposal.scope === 'frontend'" variant="outline" class="ml-1">{{
                  t('components.copilot.frontendBadge')
                }}</Badge>
              </span>
              <!-- Stakes pill (ADR-0043 / philosophy §2.3) — only while the
                   proposal is still awaiting the user's decision. -->
              <span
                v-if="
                  (state.status === 'pending' || state.status === 'applying') &&
                  state.proposal.stakesTier
                "
                :class="
                  cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-micro font-semibold',
                    state.proposal.stakesTier === 'low'
                      ? 'bg-success-soft text-success'
                      : 'bg-warning-soft text-warning',
                  )
                "
              >
                <span
                  :class="
                    cn(
                      'h-1.5 w-1.5 rounded-full',
                      state.proposal.stakesTier === 'low' ? 'bg-success' : 'bg-warning',
                    )
                  "
                ></span>
                {{
                  state.proposal.stakesTier === 'low'
                    ? t('components.copilot.stakesLow')
                    : t('components.copilot.stakesHigh')
                }}
              </span>
              <Badge v-else-if="state.status === 'applied'" variant="success">{{
                t('components.copilot.statusApplied')
              }}</Badge>
              <Badge v-else-if="state.status === 'rejected'" variant="neutral">{{
                t('components.copilot.statusRejected')
              }}</Badge>
              <Badge v-else-if="state.status === 'failed'" variant="danger">{{
                t('components.copilot.statusFailed')
              }}</Badge>
              <Badge v-else-if="state.status === 'applying'" variant="accent">{{
                t('components.copilot.statusApplying')
              }}</Badge>
            </header>

            <div class="px-4 py-3 flex flex-col gap-2">
              <!-- Backend: business data -->
              <template v-if="state.proposal.kind === 'business_data_update'">
                <p class="text-[14px] text-text">
                  {{
                    Object.entries(state.proposal.patch).length === 0
                      ? t('components.copilot.preview.businessDataChange')
                      : Object.entries(state.proposal.patch)
                          .slice(0, 3)
                          .map(([k, v]) =>
                            v === null
                              ? t('components.copilot.preview.removeKey', { key: k })
                              : t('components.copilot.preview.setKey', {
                                  key: k,
                                  value: truncate(String(v), 32),
                                }),
                          )
                          .join(', ') +
                        (Object.entries(state.proposal.patch).length > 3
                          ? t('components.copilot.preview.andMore', {
                              count: Object.entries(state.proposal.patch).length - 3,
                            })
                          : '')
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.details') }}
                  </summary>
                  <ul class="mt-2 flex flex-col gap-1">
                    <li v-for="(value, key) in state.proposal.patch" :key="key">
                      <code class="font-mono text-[11px] bg-bg px-1.5 py-0.5 rounded">{{
                        key
                      }}</code>
                      <span v-if="value === null" class="text-danger ml-2">{{
                        t('components.copilot.preview.remove')
                      }}</span>
                      <span v-else class="ml-2">→ «{{ value }}»</span>
                    </li>
                  </ul>
                </details>
              </template>

              <!-- Backend: scenario prompt -->
              <template v-else-if="state.proposal.kind === 'scenario_prompt_update'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.scenarioPromptReplace', {
                      length: state.proposal.newPrompt.length,
                    })
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.fullPrompt') }}
                  </summary>
                  <pre
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-auto"
                    >{{ state.proposal.newPrompt }}</pre
                  >
                </details>
              </template>

              <!-- Backend: project status -->
              <template v-else-if="state.proposal.kind === 'project_status_change'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.changeProjectStatus', {
                      status: statusLabel(state.proposal.status),
                    })
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Backend: pricing tier -->
              <template v-else-if="state.proposal.kind === 'scenario_pricing_tier_change'">
                <p class="text-[14px] text-text">
                  {{ t('components.copilot.preview.pricingTierPrefix') }}
                  <span class="capitalize">«{{ state.proposal.tier }}»</span>
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Backend: add slot -->
              <template v-else-if="state.proposal.kind === 'add_slot'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.addSlot', {
                      name: state.proposal.slot.displayName,
                    })
                  }}
                  <span class="text-text-muted"
                    >({{ dataTypeLabel(state.proposal.slot.dataType)
                    }}{{
                      state.proposal.slot.required === false
                        ? t('components.copilot.preview.optionalSuffix')
                        : ''
                    }})</span
                  >
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.details') }}
                  </summary>
                  <ul class="mt-2 flex flex-col gap-1">
                    <li>
                      <code class="font-mono text-[11px] bg-bg px-1.5 py-0.5 rounded">{{
                        state.proposal.slot.key
                      }}</code>
                    </li>
                    <li v-if="state.proposal.slot.description">
                      {{ state.proposal.slot.description }}
                    </li>
                    <li
                      v-if="
                        state.proposal.slot.enumValues && state.proposal.slot.enumValues.length > 0
                      "
                    >
                      {{
                        t('components.copilot.preview.slotValues', {
                          values: state.proposal.slot.enumValues.join(', '),
                        })
                      }}
                    </li>
                    <li v-if="state.proposal.slot.promptHint">
                      {{
                        t('components.copilot.preview.slotHint', {
                          hint: state.proposal.slot.promptHint,
                        })
                      }}
                    </li>
                  </ul>
                </details>
              </template>

              <!-- Backend: create scenario -->
              <template v-else-if="state.proposal.kind === 'create_scenario'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.createScenario', { name: state.proposal.name })
                  }}
                  <span class="text-text-muted"
                    >({{
                      state.proposal.direction === 'inbound'
                        ? t('components.copilot.preview.inbound')
                        : t('components.copilot.preview.outbound')
                    }})</span
                  >
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <p
                  v-if="state.proposal.slots && state.proposal.slots.length > 0"
                  class="text-caption text-text-muted"
                >
                  {{
                    t('components.copilot.preview.slots', { count: state.proposal.slots.length })
                  }}
                </p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.fullSystemPrompt') }}
                  </summary>
                  <pre
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-auto"
                    >{{ state.proposal.systemPromptTemplate }}</pre
                  >
                </details>
              </template>

              <!-- Backend: apply industry pack -->
              <template v-else-if="state.proposal.kind === 'apply_industry_pack'">
                <p class="text-[14px] text-text">
                  {{ t('components.copilot.preview.applyPack') }}
                  <code class="font-mono text-[12px] bg-bg px-1.5 py-0.5 rounded ml-1"
                    >{{ state.proposal.packIdn }}@{{ state.proposal.packVersion }}</code
                  >
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <p class="text-caption text-text-muted">
                  {{ t('components.copilot.preview.applyPackHint') }}
                </p>
              </template>

              <!-- Backend: create script -->
              <template v-else-if="state.proposal.kind === 'create_script'">
                <p class="text-[14px] text-text">
                  {{ t('components.copilot.preview.createScript', { name: state.proposal.name }) }}
                  <span class="text-text-muted">
                    ({{ state.proposal.scriptKind
                    }}<template v-if="state.proposal.interceptEvent"
                      >, {{ state.proposal.interceptEvent }}</template
                    >, scope: {{ state.proposal.scopeKind }})
                  </span>
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.scriptCode') }}
                  </summary>
                  <pre
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-auto"
                    >{{ state.proposal.code }}</pre
                  >
                </details>
              </template>

              <!-- Backend: update script -->
              <template v-else-if="state.proposal.kind === 'update_script'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.updateScript', {
                      length: state.proposal.newCode.length,
                    })
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.newCode') }}
                  </summary>
                  <pre
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[280px] overflow-auto"
                    >{{ state.proposal.newCode }}</pre
                  >
                </details>
              </template>

              <!-- Backend: create step (per ADR-0022) -->
              <template v-else-if="state.proposal.kind === 'create_step'">
                <p class="text-[14px] text-text">
                  {{ t('components.copilot.preview.createStep', { name: state.proposal.name }) }}
                  <span class="text-text-muted">({{ state.proposal.stepKind }})</span>
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.details') }}
                  </summary>
                  <ul class="mt-2 flex flex-col gap-1">
                    <li>
                      idn:
                      <code class="font-mono text-[11px] bg-bg px-1.5 py-0.5 rounded">{{
                        state.proposal.idn
                      }}</code>
                    </li>
                    <li v-if="state.proposal.promptFragment">
                      {{
                        t('components.copilot.preview.promptCharCount', {
                          length: state.proposal.promptFragment.length,
                        })
                      }}
                    </li>
                    <li>
                      transitions:
                      <code class="font-mono text-[11px] bg-bg px-1.5 py-0.5 rounded">{{
                        JSON.stringify(state.proposal.transitions)
                      }}</code>
                    </li>
                  </ul>
                  <pre
                    v-if="state.proposal.promptFragment"
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-auto"
                    >{{ state.proposal.promptFragment }}</pre
                  >
                </details>
              </template>

              <!-- Backend: update step -->
              <template v-else-if="state.proposal.kind === 'update_step'">
                <p class="text-[14px] text-text">
                  {{ t('components.copilot.preview.updateStep') }}
                  <span class="text-text-muted">
                    ({{
                      t('components.copilot.preview.fields', {
                        count: Object.keys(state.proposal.patch).length,
                      })
                    }})
                  </span>
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
                <details class="text-caption text-text-muted">
                  <summary class="cursor-pointer hover:text-text">
                    {{ t('components.copilot.preview.changes') }}
                  </summary>
                  <ul class="mt-2 flex flex-col gap-1">
                    <li v-for="(v, k) in state.proposal.patch" :key="k">
                      <code class="font-mono text-[11px] bg-bg px-1.5 py-0.5 rounded">{{ k }}</code>
                      <span class="ml-2">{{
                        typeof v === 'string' ? truncate(v, 64) : JSON.stringify(v)
                      }}</span>
                    </li>
                  </ul>
                  <pre
                    v-if="typeof state.proposal.patch.promptFragment === 'string'"
                    class="mt-2 p-3 bg-bg border border-border rounded-lg text-[12px] font-mono leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-auto"
                    >{{ state.proposal.patch.promptFragment }}</pre
                  >
                </details>
              </template>

              <!-- Frontend: navigate -->
              <template v-else-if="state.proposal.kind === 'navigate'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.navigateTo', {
                      target: navigateLabel(state.proposal.target),
                    })
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Frontend: theme -->
              <template v-else-if="state.proposal.kind === 'set_theme'">
                <p class="text-[14px] text-text">
                  {{
                    t('components.copilot.preview.setTheme', {
                      theme: themeLabel(state.proposal.theme),
                    })
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Frontend: developer mode -->
              <template v-else-if="state.proposal.kind === 'toggle_developer_mode'">
                <p class="text-[14px] text-text">
                  {{
                    state.proposal.enabled
                      ? t('components.copilot.preview.devModeOn')
                      : t('components.copilot.preview.devModeOff')
                  }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Frontend: clarification question. Usually rendered by
                   mode-specific pages, but keep a readable fallback here
                   so a proposal never becomes a header-only card. -->
              <template v-else-if="state.proposal.kind === 'clarification_question'">
                <p class="text-[14px] text-text whitespace-pre-wrap">
                  {{
                    state.proposal.question ||
                    t('components.copilot.clarification.fallbackQuestion')
                  }}
                </p>
                <ol
                  v-if="clarificationSuggestions(state.proposal.suggestions).length > 0"
                  class="list-decimal list-inside text-[13px] text-text-muted space-y-1"
                >
                  <li
                    v-for="(suggestion, si) in clarificationSuggestions(state.proposal.suggestions)"
                    :key="si"
                  >
                    {{ suggestion }}
                  </li>
                </ol>
                <p v-else class="text-caption text-text-muted">
                  {{ t('components.copilot.clarification.missingOptions') }}
                </p>
                <p class="text-caption text-text-muted">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Frontend: simulated call transcript (ADR-0047). Standalone
                   path keeps the existing chat-bubble strip and adds the
                   optional caller-persona line + exercise chips when the
                   server supplies them. Lone halves of a `pairId` (partner
                   hasn't streamed in yet) get a hint inline. -->
              <template v-else-if="state.proposal.kind === 'simulated_call_transcript'">
                <p
                  v-if="isLonePairHalf(state.proposal)"
                  class="text-caption text-text-muted italic"
                >
                  ({{ t('components.copilot.simulatedCall.pendingPairHint') }})
                </p>
                <p
                  v-if="state.proposal.callerProfile"
                  class="text-caption text-text-muted italic"
                >
                  {{ state.proposal.callerProfile }}
                </p>
                <div
                  v-if="state.proposal.exercises && state.proposal.exercises.length > 0"
                  class="flex flex-wrap gap-1.5"
                >
                  <span
                    v-for="(ex, ei) in state.proposal.exercises"
                    :key="ei"
                    class="inline-flex items-center rounded-full border border-border bg-bg px-2 py-0.5 text-[11px] text-text-muted"
                  >
                    {{ ex }}
                  </span>
                </div>
                <SimulatedCallBubbles :exchange="state.proposal.exchange" />
                <p class="text-caption text-text-muted italic">{{ state.proposal.rationale }}</p>
              </template>

              <!-- Frontend: artifact walkthrough — explain-in-reverse (ADR-0047) -->
              <template v-else-if="state.proposal.kind === 'artifact_walkthrough'">
                <div class="flex flex-col gap-3">
                  <header class="text-[11px] uppercase tracking-wide font-medium text-text-muted">
                    {{ t('components.copilot.artifactWalkthrough.header') }}
                  </header>
                  <p class="text-[14px] text-text whitespace-pre-wrap leading-snug">
                    {{ state.proposal.businessSummary }}
                  </p>
                  <SimulatedCallBubbles :exchange="state.proposal.simulatedCall.exchange" />
                  <p
                    v-if="state.proposal.simulatedCall.rationale"
                    class="text-caption text-text-muted italic"
                  >
                    {{ state.proposal.simulatedCall.rationale }}
                  </p>
                </div>
              </template>

              <p v-if="state.error" class="text-caption text-danger">{{ state.error }}</p>

              <!-- Per ADR-0045 v1 + ADR-0063 v2 / philosophy §2.7. Numeric
                   cost/risk forecast strip. Shown only when the server
                   attached one (no causal model → strip hidden, no
                   synthesized stub). -->
              <div
                v-if="
                  state.proposal.forecast &&
                  (state.proposal.forecast.cost || state.proposal.forecast.risk)
                "
                class="flex gap-2 pt-2"
              >
                <!-- Cost box -->
                <div
                  v-if="state.proposal.forecast.cost"
                  class="flex-1 rounded-xl border border-border bg-bg-2 px-3 py-2.5"
                >
                  <div
                    class="flex items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-text-subtle"
                  >
                    <span>{{ costEmoji(state.proposal.forecast.cost.perCallCentsDelta) }}</span>
                    {{ t('components.copilot.forecast.costTitle') }}
                  </div>
                  <div class="mt-1 text-caption text-text">
                    <strong class="font-semibold">{{
                      costLabel(state.proposal.forecast.cost.perCallCentsDelta)
                    }}</strong>
                    <span class="text-text-muted"> · {{ state.proposal.forecast.cost.rationale }}</span>
                    <span
                      v-if="confidenceQualifier(state.proposal.forecast.cost.confidence)"
                      class="text-text-subtle"
                    >
                      {{ confidenceQualifier(state.proposal.forecast.cost.confidence) }}
                    </span>
                  </div>
                </div>
                <!-- Trade-off / risk box -->
                <div
                  v-if="state.proposal.forecast.risk"
                  class="flex-[1.4] rounded-xl border border-border bg-bg-2 px-3 py-2.5"
                >
                  <div
                    class="flex items-center gap-1.5 text-micro font-semibold uppercase tracking-wide text-text-subtle"
                  >
                    <span>{{ riskEmoji(state.proposal.forecast.risk.direction) }}</span>
                    {{ t('components.copilot.forecast.tradeoffTitle') }}
                  </div>
                  <div class="mt-1 text-caption text-text">
                    <strong class="font-semibold"
                      >{{ riskLabel(state.proposal.forecast.risk.direction) }}.</strong
                    >
                    <span class="text-text-muted"> {{ state.proposal.forecast.risk.rationale }}</span>
                  </div>
                </div>
              </div>
            </div>

            <footer
              v-if="state.status === 'pending' || state.status === 'applying'"
              class="px-4 py-2.5 border-t border-border bg-bg-2/50 flex items-center gap-2 justify-end"
            >
              <Button
                variant="ghost"
                size="sm"
                :disabled="state.status !== 'pending'"
                @click="onReject(state)"
              >
                <X class="h-3.5 w-3.5" />
                {{ t('components.copilot.reject') }}
              </Button>
              <Button size="sm" :loading="state.status === 'applying'" @click="onApply(state)">
                <Check v-if="state.status === 'pending'" class="h-3.5 w-3.5" />
                {{ t('components.copilot.apply') }}
              </Button>
            </footer>

            <!-- Silent-apply footer (ADR-0043 / philosophy §2.3). The
                 proposal was auto-applied on arrival; expose Undo. -->
            <footer
              v-else-if="state.status === 'silent_applied'"
              class="px-4 py-2.5 border-t border-success/20 bg-success-soft flex items-center gap-2 justify-between"
            >
              <span class="text-caption font-medium text-success inline-flex items-center gap-1.5">
                <Check class="h-3.5 w-3.5" />
                {{ t('components.copilot.silentApplied') }}
              </span>
              <Button v-if="state.undo" variant="ghost" size="sm" @click="onUndo(state)">
                {{ t('components.copilot.undo') }}
              </Button>
            </footer>

            <footer
              v-else-if="state.status === 'reverted'"
              class="px-4 py-2.5 border-t border-border bg-bg-2/50 flex items-center gap-2"
            >
              <span class="text-caption text-text-muted inline-flex items-center gap-1.5">
                <X class="h-3.5 w-3.5" />
                {{ t('components.copilot.reverted') }}
              </span>
            </footer>
          </div>
          </template>
          </template>
        </template>
      </template>
    </div>

    <template #footer>
      <!-- Composer rides only the text channel for v1. Channel-pick and
           the voice-stub render their own primary actions in the body. -->
      <div v-if="assistantChannel === 'text'" class="flex items-end gap-2 w-full">
        <textarea
          ref="inputEl"
          v-model="input"
          rows="1"
          :placeholder="
            streaming
              ? t('components.copilot.replying')
              : t('components.copilot.composerPlaceholder')
          "
          :disabled="streaming"
          class="flex-1 resize-none bg-surface border border-border rounded-xl px-3.5 py-2.5 text-[14px] leading-relaxed focus-visible:outline-none focus-visible:border-accent focus-visible:ring-4 focus-visible:ring-accent/15 disabled:opacity-60"
          @input="autoSize"
          @keydown="onKeydown"
        />
        <Button
          size="icon"
          :disabled="!input.trim() || streaming"
          :aria-label="t('components.copilot.send')"
          @click="send"
        >
          <ArrowUp class="h-4 w-4" />
        </Button>
      </div>
    </template>
  </Sheet>
</template>
